'use client';

import { Check, Minus } from 'lucide-react';
import * as React from 'react';

import { cn } from '@ui/lib/utils';

import { Button } from '../../../common/button';
import { ResponsivePopover } from '../../../common/responsivePopover';
import { Checkbox } from '../../../ui/checkbox';

// Default empty array for value prop
const DEFAULT_VALUE: string[] = [];

interface MultiSelectFilterCompactProps {
  label: string;
  icon: React.ReactNode;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  /**
   * When set together with `onNegativeChange`, the control becomes
   * tri-state: a click cycles unchecked → positive → negative →
   * unchecked. Negative selections appear with a red "−" indicator
   * and are surfaced in the trigger label with a leading "− " prefix.
   */
  negativeValue?: string[];
  onNegativeChange?: (value: string[]) => void;
  className?: string;
  clearText?: string;
  applyText?: string;
  showClear?: boolean;
  /**
   * When true, the label/text is hidden on small screens (< sm) and only the
   * icon is shown. The label remains visible on sm and larger breakpoints.
   */
  hideLabelOnMobile?: boolean;
}

export const MultiSelectFilterCompact = ({
  label,
  icon,
  options,
  value = DEFAULT_VALUE,
  onChange,
  negativeValue,
  onNegativeChange,
  className,
  clearText = 'Clear',
  applyText = 'Apply',
  showClear = false,
  hideLabelOnMobile = false
}: MultiSelectFilterCompactProps) => {
  const [open, setOpen] = React.useState(false);

  const triState = !!negativeValue && !!onNegativeChange;
  const negValue = negativeValue ?? DEFAULT_VALUE;

  const handleClick = (optionValue: string) => {
    const isPositive = value.includes(optionValue);
    const isNegative = negValue.includes(optionValue);

    if (!triState) {
      if (isPositive) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
      return;
    }

    // Cycle: unchecked → positive → negative → unchecked.
    if (!isPositive && !isNegative) {
      onChange([...value, optionValue]);
    } else if (isPositive) {
      onChange(value.filter((v) => v !== optionValue));
      onNegativeChange!([...negValue, optionValue]);
    } else {
      onNegativeChange!(negValue.filter((v) => v !== optionValue));
    }
  };

  // Determine display text based on selections (positive + negative).
  const positiveOptions = options.filter((o) => value.includes(o.value));
  const negativeOptions = options.filter((o) => negValue.includes(o.value));
  const totalSelected = positiveOptions.length + negativeOptions.length;
  let displayText: string;

  if (totalSelected === 0) {
    displayText = label;
  } else if (totalSelected === 1) {
    displayText = positiveOptions[0] ? positiveOptions[0].label : `− ${negativeOptions[0].label}`;
  } else {
    displayText = `${totalSelected} selected`;
  }

  const isSelected = totalSelected > 0;

  const triggerButton = (
    <button
      type="button"
      className={cn(
        'flex items-center flex-row gap-2 px-3 py-2 border rounded-lg transition-colors cursor-pointer min-w-0',
        hideLabelOnMobile ? 'justify-center sm:justify-start' : '',
        isSelected
          ? 'bg-primary-0 border-primary-200 hover:border-primary-300 [&_svg]:!text-primary-500'
          : 'bg-white border-neutral-200 hover:border-neutral-300',
        className
      )}
    >
      <span className={cn('flex-shrink-0 flex items-center', isSelected ? 'text-primary-500' : '')}>{icon}</span>
      <span
        className={cn(
          'text-sm font-normal truncate min-w-0',
          hideLabelOnMobile ? 'hidden sm:inline' : '',
          isSelected ? 'text-primary-500' : 'text-neutral-900'
        )}
      >
        {displayText}
      </span>
    </button>
  );

  return (
    <ResponsivePopover
      open={open}
      onOpenChange={setOpen}
      trigger={triggerButton}
      title={label}
      contentClassName="w-[220px] p-2"
      footer={
        showClear ? (
          <div className="flex gap-2 border-t border-neutral-200 pt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                onChange([]);
                if (triState) onNegativeChange!([]);
              }}
              className="flex-1"
              label={clearText}
              disabled={totalSelected === 0}
            />
            <Button variant="primary" size="sm" onClick={() => setOpen(false)} className="flex-1" label={applyText} />
          </div>
        ) : undefined
      }
    >
      <div className="space-y-2">
        {options.map((option) => {
          const isPositive = value.includes(option.value);
          const isNegative = negValue.includes(option.value);
          return (
            <label
              key={option.value}
              className="flex items-center space-x-2 cursor-pointer hover:bg-neutral-100 p-2 rounded"
              onClick={
                triState
                  ? (e) => {
                      e.preventDefault();
                      handleClick(option.value);
                    }
                  : undefined
              }
            >
              {triState ? (
                <span
                  className={cn(
                    'h-4 w-4 shrink-0 rounded border flex items-center justify-center transition-colors',
                    isPositive && 'bg-primary-500 border-primary-500 text-white',
                    isNegative && 'bg-red-500 border-red-500 text-white',
                    !isPositive && !isNegative && 'bg-white border-neutral-300'
                  )}
                  aria-hidden="true"
                >
                  {isPositive && <Check className="h-3 w-3" strokeWidth={3} />}
                  {isNegative && <Minus className="h-3 w-3" strokeWidth={3} />}
                </span>
              ) : (
                <Checkbox checked={isPositive} onCheckedChange={() => handleClick(option.value)} />
              )}
              <span
                className={cn(
                  'text-sm font-normal text-neutral-900',
                  isNegative && 'text-red-700 line-through decoration-red-300'
                )}
              >
                {option.label}
              </span>
            </label>
          );
        })}
      </div>
    </ResponsivePopover>
  );
};
